import React, { useState } from 'react';
import { Upload, Button, message } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

interface ImageUploadProps {
  value?: string;
  onChange?: (value: string) => void;
  maxSize?: number; // in MB
  aspectRatio?: string; // e.g., "16:9", "1:1"
  placeholder?: string;
}

/**
 * ImageUpload - Converts uploaded images to base64 for localStorage storage
 * Supports PNG, JPEG, WebP, SVG
 */
const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  maxSize = 2,
  aspectRatio,
  placeholder = 'Upload Image',
}) => {
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>(value || '');

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    const isLtMaxSize = file.size / 1024 / 1024 < maxSize;
    if (!isLtMaxSize) {
      message.error(`Image must be smaller than ${maxSize}MB!`);
      return false;
    }

    // Convert to base64
    setLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setImageUrl(base64);
      setLoading(false);
      onChange?.(base64);
      message.success('Image uploaded successfully!');
    };
    reader.onerror = () => {
      setLoading(false);
      message.error('Failed to read image file');
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto upload
  };

  const handleRemove = () => {
    setImageUrl('');
    onChange?.('');
    message.success('Image removed');
  };

  return (
    <div>
      {imageUrl ? (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={imageUrl}
            alt="Uploaded"
            style={{
              maxWidth: '100%',
              maxHeight: '200px',
              objectFit: 'contain',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '8px',
              backgroundColor: '#f9fafb',
            }}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleRemove}
            style={{ marginTop: '8px', display: 'block' }}
          >
            Remove Image
          </Button>
        </div>
      ) : (
        <Upload
          beforeUpload={beforeUpload}
          showUploadList={false}
          accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
        >
          <Button icon={<UploadOutlined />} loading={loading}>
            {placeholder}
          </Button>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
            PNG, JPEG, WebP, or SVG (max {maxSize}MB)
            {aspectRatio && ` • Recommended: ${aspectRatio}`}
          </div>
        </Upload>
      )}
    </div>
  );
};

export default ImageUpload;
